import { useCallback, useEffect, useRef, useState } from 'react'
import 'rrweb/dist/style.css'
import { api, ErroreApi } from '../servizi/api.ts'
import { decifraBlocco } from '../servizi/registrazione.ts'
import { Attesa, Avviso, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, Modale, Paginazione, Tabella, totaleDi,
} from '../componenti/base.tsx'
import { useSessione } from '../servizi/sessione.tsx'

/**
 * Registrazioni dello schermo: elenco, richiesta di visione, riproduzione, blocco legale.
 *
 * La visione richiede l'approvazione di un utente diverso dal richiedente e una motivazione
 * scritta, e produce una notifica all'operatore ripreso. I vincoli sono applicati dall'API:
 * l'interfaccia si limita a rappresentarli.
 */

type Voce = {
  id: string
  utente_id: string
  nome: string
  cognome: string
  avviata_il: string
  terminata_il: string | null
  modalita: 'continua' | 'assistenza'
  chunk: number
  bytes: string
  scade_il: string
  legal_hold_id: string | null
  /** Righe totali prima della finestra, aggiunto dalla rotta di elenco. */
  totale?: string | number
}

type Approvazione = {
  id: string
  tipo: string
  oggetto_id: string
  stato: string
  motivazione: string
}

const formato = new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'short' })

/** Righe per pagina. Resta entro il massimo accettato dalla rotta. */
const LIMITE = 50

function peso(bytes: string) {
  const n = Number(bytes)
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function Registrazioni() {
  const { puo } = useSessione()
  const [voci, setVoci] = useState<Voce[] | null>(null)
  const [approvazioni, setApprovazioni] = useState<Approvazione[]>([])
  const [errore, setErrore] = useState<string | null>(null)
  const [apertura, setApertura] = useState<Voce | null>(null)
  const [salta, setSalta] = useState(0)

  const carica = useCallback(async () => {
    try {
      // Le approvazioni non sono un elenco mostrato ma la provvista da cui si ricava il
      // comando di apertura di ogni riga. Si chiedono al solo tipo che serve: filtrare
      // l'intera coda lato interfaccia lasciava fuori le approvazioni oltre la prima
      // finestra, e la riga corrispondente restava senza comando di riproduzione.
      const [r, a] = await Promise.all([
        api.get<Voce[]>(`/registrazioni?limite=${LIMITE}&salta=${salta}`),
        api.get<Approvazione[]>(
          '/approvazioni?stato=approvata&tipo=visione_registrazione&limite=200',
        ).catch(() => [] as Approvazione[]),
      ])
      setVoci(r)
      setApprovazioni(a)
    } catch (e) {
      setErrore(e instanceof Error ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
      setVoci([])
    }
  }, [salta])

  useEffect(() => { void carica() }, [carica])

  const approvazioneDi = (id: string) => approvazioni.find((a) => a.oggetto_id === id) ?? null

  if (!voci) return <Attesa testo="Caricamento…" />

  return (
    <>
      <Intestazione
        titolo="Registrazioni dello schermo"
        sottotitolo="Le visioni sono approvate da un utente diverso dal richiedente e notificate all’operatore ripreso."
      />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}

      <Avviso tipo="attenzione">
        La registrazione dello schermo è una misura di controllo a distanza. Resta lecita finché
        valgono i presupposti dell’art. 4 dello Statuto dei lavoratori: accordo sindacale o
        autorizzazione dell’Ispettorato, informativa ai lavoratori, valutazione d’impatto.
        Le registrazioni si cancellano alla scadenza; la conservazione oltre il termine richiede
        un blocco legale motivato.
      </Avviso>

      <Tabella
        intestazioni={['Operatore', 'Inizio', 'Durata', 'Modalità', 'Peso', 'Scade',
          <span key="comandi" className="solo-lettore">Comandi</span>]}
        vuoto={<Vuoto testo="Nessuna registrazione." />}
      >
        {voci.map((v) => {
          const approvata = approvazioneDi(v.id)
          const durata = v.terminata_il
            ? Math.round((+new Date(v.terminata_il) - +new Date(v.avviata_il)) / 60000)
            : null
          return (
            <tr key={v.id}>
              <td data-etichetta="Operatore">{v.cognome} {v.nome}</td>
              <td data-etichetta="Inizio">{formato.format(new Date(v.avviata_il))}</td>
              <td data-etichetta="Durata">
                {durata === null ? <span className="tenue">in corso</span> : `${durata} min`}
              </td>
              <td data-etichetta="Modalità">
                <Etichetta tono={v.modalita === 'assistenza' ? 'accento' : 'neutro'}>
                  {v.modalita === 'assistenza' ? 'assistenza' : 'continua'}
                </Etichetta>
              </td>
              <td data-etichetta="Peso">{peso(v.bytes)}</td>
              <td data-etichetta="Scade">
                {new Date(v.scade_il).toLocaleDateString('it-IT')}
                {v.legal_hold_id && <> <Etichetta tono="critico">blocco legale</Etichetta></>}
              </td>
              <td data-etichetta="Comandi" className="a-destra">
                <div className="riga riga-a-destra">
                  {puo('registrazione.visiona') && (
                    approvata
                      ? <Bottone variante="primario" dimensione="piccolo"
                                 onClick={() => setApertura(v)}>Apri</Bottone>
                      : <ChiediApprovazione voce={v} fatto={carica} />
                  )}
                  {puo('registrazione.conserva') && !v.legal_hold_id && (
                    <Conserva voce={v} fatto={carica} />
                  )}
                </div>
              </td>
            </tr>
          )
        })}
      </Tabella>

      <Paginazione salta={salta} limite={LIMITE} totale={totaleDi(voci)}
                   ricevute={voci.length} cambia={setSalta} />

      {apertura && (
        <Lettore
          voce={apertura}
          approvazione={approvazioneDi(apertura.id)!}
          chiudi={() => setApertura(null)}
        />
      )}
    </>
  )
}

/** Richiesta di visione: la motivazione è obbligatoria e decide un altro utente. */
function ChiediApprovazione({ voce, fatto }: { voce: Voce; fatto: () => Promise<void> }) {
  const [aperto, setAperto] = useState(false)
  const [motivazione, setMotivazione] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)

  if (esito) return <span className="piccolo tenue">{esito}</span>
  if (!aperto) {
    return (
      <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAperto(true)}>
        Richiedi la visione
      </Bottone>
    )
  }

  return (
    <form
      className="riga modulo-in-riga"
      onSubmit={async (e) => {
        e.preventDefault()
        setInCorso(true)
        try {
          await api.post('/approvazioni', {
            tipo: 'visione_registrazione', oggetto_tipo: 'registrazione',
            oggetto_id: voce.id, motivazione, giorni_validita: 7,
          })
          setEsito('Richiesta inviata.')
          await fatto()
        } catch (err) {
          setEsito(err instanceof ErroreApi ? err.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
        } finally {
          setInCorso(false)
        }
      }}
    >
      <Campo etichetta="Motivazione della richiesta" valore={motivazione} cambia={setMotivazione}
             obbligatorio minLength={10} />
      <Bottone tipo="submit" variante="primario" dimensione="piccolo" disabled={inCorso}>
        Invia
      </Bottone>
      <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAperto(false)}>
        Annulla
      </Bottone>
    </form>
  )
}

/**
 * Conservazione oltre la scadenza.
 *
 * Richiede il fatto documentato che la rende necessaria e la durata del blocco; l'operatore
 * ripreso riceve una notifica. Scaduto il termine la cancellazione automatica riprende.
 */
function Conserva({ voce, fatto }: { voce: Voce; fatto: () => Promise<void> }) {
  const [aperto, setAperto] = useState(false)
  const [evento, setEvento] = useState('')
  const [motivazione, setMotivazione] = useState('')
  const [giorni, setGiorni] = useState(90)
  const [esito, setEsito] = useState<string | null>(null)

  if (esito) return <span className="piccolo tenue">{esito}</span>
  if (!aperto) {
    return (
      <Bottone variante="discreto" dimensione="piccolo" onClick={() => setAperto(true)}>
        Conserva
      </Bottone>
    )
  }

  return (
    <Modale titolo="Conservazione oltre la scadenza" chiudi={() => setAperto(false)}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          try {
            await api.post(`/registrazioni/${voce.id}/conserva`, {
              evento_documentato: evento, motivazione, giorni,
            })
            setEsito('Conservazione registrata.')
            await fatto()
          } catch (err) {
            setEsito(err instanceof ErroreApi ? err.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
          }
        }}
      >
        <Avviso tipo="attenzione">
          Il blocco legale sospende la cancellazione automatica. Richiede un fatto documentato:
          un procedimento, una segnalazione, una richiesta dell’autorità.
        </Avviso>
        <Campo etichetta="Fatto documentato" valore={evento} cambia={setEvento}
               obbligatorio minLength={5} />
        <AreaTesto etichetta="Motivazione" valore={motivazione} cambia={setMotivazione}
                   righe={3} obbligatorio aiuto="Almeno 20 caratteri." />
        <Campo etichetta="Durata (giorni)" tipo="number" valore={giorni}
               cambia={(v) => setGiorni(Number(v))} min={1} max={365} />
        <div className="azioni-modulo">
          <Bottone variante="discreto" onClick={() => setAperto(false)}>Annulla</Bottone>
          <Bottone tipo="submit" variante="primario"
                   disabled={motivazione.trim().length < 20 || evento.trim().length < 5}>
            Conserva
          </Bottone>
        </div>
      </form>
    </Modale>
  )
}

/**
 * Il lettore.
 *
 * L'API consegna gli indirizzi dei blocchi e la chiave; il browser scarica, decifra e rimette
 * insieme il filmato. I blocchi non vengono decifrati sul server: chi amministra
 * l'infrastruttura dispone dei soli byte cifrati.
 */
function Lettore({ voce, approvazione, chiudi }: {
  voce: Voce; approvazione: Approvazione; chiudi: () => void
}) {
  const contenitore = useRef<HTMLDivElement>(null)
  const [stato, setStato] = useState<'motivazione' | 'carico' | 'pronto' | 'errore'>('motivazione')
  const [motivazione, setMotivazione] = useState(approvazione.motivazione ?? '')
  const [messaggio, setMessaggio] = useState<string | null>(null)

  const riproduci = async () => {
    setStato('carico')
    try {
      const r = await api.post<{ chiave: string; pezzi: string[] }>(
        `/registrazioni/${voce.id}/visiona`,
        { approvazione_id: approvazione.id, motivazione },
      )
      const eventi: unknown[] = []
      for (const url of r.pezzi) {
        const risposta = await fetch(url)
        if (!risposta.ok) continue           // un blocco mancante non deve fermare il resto
        try { eventi.push(...await decifraBlocco(r.chiave, await risposta.arrayBuffer())) }
        catch { /* blocco illeggibile: si salta */ }
      }
      if (eventi.length < 2) {
        setMessaggio('La registrazione non contiene abbastanza dati per essere riprodotta.')
        setStato('errore')
        return
      }
      const { Replayer } = await import('rrweb')
      setStato('pronto')
      // Il nodo esiste solo dopo il render dello stato "pronto".
      queueMicrotask(() => {
        if (!contenitore.current) return
        contenitore.current.innerHTML = ''
        const lettore = new Replayer(eventi as never[], {
          root: contenitore.current, speed: 1, showWarning: false, mouseTail: false,
        })
        lettore.play()
      })
    } catch (e) {
      setMessaggio(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')
      setStato('errore')
    }
  }

  return (
    <Modale
      larga
      titolo={`${voce.cognome} ${voce.nome} — ${formato.format(new Date(voce.avviata_il))}`}
      chiudi={chiudi}
      azioni={stato === 'motivazione' ? (
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" disabled={motivazione.trim().length < 20}
                   onClick={() => void riproduci()}>Apri la registrazione</Bottone>
        </>
      ) : undefined}
    >
      {stato === 'motivazione' && (
        <>
          <Avviso tipo="attenzione">
            L’apertura della registrazione scrive un evento di sicurezza e invia una notifica
            a <strong>{voce.nome} {voce.cognome}</strong>, con la motivazione riportata qui sotto.
          </Avviso>
          <AreaTesto etichetta="Motivazione" valore={motivazione} cambia={setMotivazione}
                     righe={3} aiuto="Almeno 20 caratteri." />
        </>
      )}

      {stato === 'carico' && <Attesa testo="Caricamento della registrazione…" />}
      {stato === 'errore' && <Avviso tipo="errore">{messaggio}</Avviso>}
      {stato === 'pronto' && (
        <>
          <div className="riquadro-lettore" ref={contenitore} />
          <p className="piccolo tenue">
            Tutti i testi sono mascherati: la registrazione mostra l’uso dell’applicazione, non
            il contenuto delle schede.
          </p>
        </>
      )}
    </Modale>
  )
}
